import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Editor, { type BeforeMount, type OnMount } from '@monaco-editor/react';

import type { CustomLibrary } from '@/shared/types';

import type { ModuleTarget } from '@/entities/langserver/api/types';

import type { AutoImport } from '@/features/code-analysis';
import { PythonLanguageService } from '@/features/code-analysis';

import styles from './CodeCell.module.css';

type CodeCellProps = {
  code: string;
  onCodeChange?: (code: string) => void;
  readOnly?: boolean;
  scrollableReadOnly?: boolean;
  customLibraries?: CustomLibrary[];
  autoImports?: AutoImport[];
  clientId?: string;
  signature?: string;
  target?: ModuleTarget;
  style?: React.CSSProperties;
};

type ParsedFunction = {
  name: string;
  params: string;
  docstring?: string;
};

const parseParamsToSnippet = (params: string): string => {
  if (!params.trim()) return '';

  const paramList = params.split(',').map((p) => p.trim());
  const snippetParams = paramList
    .map((param, index) => {
      const paramName = param.split(':')[0].split('=')[0].trim();
      return `\${${index + 1}:${paramName}}`;
    })
    .join(', ');

  return snippetParams;
};

const parsePythonFunctions = (content: string): ParsedFunction[] => {
  const functions: ParsedFunction[] = [];
  const functionRegex = /def\s+(\w+)\s*\(([^)]*)\)\s*(?:->\s*[^:]+)?:/g;

  let match;
  while ((match = functionRegex.exec(content)) !== null) {
    const name = match[1];
    const params = match[2];
    const startIndex = match.index;
    const nextLineStart = content.indexOf('\n', startIndex) + 1;

    let docstring = '';
    if (nextLineStart > 0) {
      const docstringMatch = content.substring(nextLineStart).match(/^\s*("""[\s\S]*?"""|'''[\s\S]*?''')/);
      if (docstringMatch) {
        docstring = docstringMatch[1]
          .replace(/^("""|''')/, '')
          .replace(/("""|''')$/, '')
          .trim();
      }
    }

    functions.push({ name, params, docstring });
  }

  return functions;
};

const CODE_CELL_THEME = 'cyclone-light';
const READONLY_CODE_CELL_THEME = 'cyclone-light-readonly';

function getCssColor(variableName: string, fallback: string) {
  if (typeof document === 'undefined') return fallback;

  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim() || fallback;
}

function getCodeCellThemeColors() {
  return {
    editorBackground: getCssColor('--opds-sem-color-container-standard-alt-level1', '#ffffff'),
    editorForeground: getCssColor('--opds-sem-color-onContainer-standard-level1', '#101010'),
    gutterBackground: getCssColor('--opds-sem-color-container-standard-opacity-level1', '#0000000a'),
    lineNumberForeground: getCssColor('--opds-sem-color-onContainer-standard-level7', '#b2b2b2'),
    errorForeground: getCssColor('--opds-sem-color-container-none-critical-level2', '#ed3143'),
    warningForeground: getCssColor('--opds-sem-color-container-state-caution-level3', '#fab719'),
    infoForeground: getCssColor('--opds-sem-color-container-colorful-blue-level4', '#318fed'),
  };
}

const defineCodeCellThemes: BeforeMount = (monaco) => {
  const colors = getCodeCellThemeColors();
  const themeColors = {
    'editor.background': colors.editorBackground,
    'editor.foreground': colors.editorForeground,
    'editorGutter.background': colors.gutterBackground,
    'editorLineNumber.foreground': colors.lineNumberForeground,
    'editorLineNumber.activeForeground': colors.editorForeground,
    'editor.lineHighlightBackground': '#00000000',
    'editor.lineHighlightBorder': '#00000000',
    'editorError.foreground': colors.errorForeground,
    'editorWarning.foreground': colors.warningForeground,
    'editorInfo.foreground': colors.infoForeground,
  };

  monaco.editor.defineTheme(CODE_CELL_THEME, {
    base: 'vs',
    inherit: true,
    rules: [],
    colors: { ...themeColors },
  });

  monaco.editor.defineTheme(READONLY_CODE_CELL_THEME, {
    base: 'vs',
    inherit: true,
    rules: [],
    colors: { ...themeColors },
  });
};

export const CodeCell: React.FC<CodeCellProps> = ({
  code,
  onCodeChange,
  readOnly = false,
  scrollableReadOnly = false,
  customLibraries = [],
  autoImports = [],
  clientId,
  signature = '',
  target = 'run',
  style,
}) => {
  const editorHeight = useMemo(() => {
    if (!readOnly || scrollableReadOnly) return '100%';

    const lineCount = code.split('\n').length;
    const lineHeight = 24;
    const paddingTop = 16;
    const paddingBottom = 8;
    return `${lineCount * lineHeight + paddingTop + paddingBottom}px`;
  }, [code, readOnly, scrollableReadOnly]);

  const editorStyle = useMemo(() => {
    const shouldUseFlexHeight = !readOnly || scrollableReadOnly;

    if (style) return style;

    if (shouldUseFlexHeight) {
      return { flex: 1 };
    }

    return {
      height: editorHeight,
      minHeight: editorHeight,
    };
  }, [readOnly, editorHeight, scrollableReadOnly, style]);

  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
  const monacoRef = useRef<Parameters<OnMount>[1] | null>(null);
  const completionProviderRef = useRef<{ dispose: () => void } | null>(null);
  const langServiceRef = useRef<PythonLanguageService | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);

  const registerCustomCompletions = useCallback(
    (monaco: Parameters<OnMount>[1]) => {
      if (completionProviderRef.current) {
        completionProviderRef.current.dispose();
      }

      type Monaco = Parameters<OnMount>[1];
      type ITextModel = Monaco['editor']['ITextModel'];
      type Position = Monaco['Position'];

      completionProviderRef.current = monaco.languages.registerCompletionItemProvider('python', {
        provideCompletionItems: (model: ITextModel, position: Position) => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };

          type CompletionItem = {
            label: string;
            kind: number;
            documentation: string;
            insertText: string;
            insertTextRules?: number;
            range: typeof range;
            sortText?: string;
          };

          const customSuggestions: CompletionItem[] = customLibraries.flatMap((lib) => {
            const functions = parsePythonFunctions(lib.content);
            const moduleName = lib.name.replace('.py', '');

            return functions.flatMap((func) => {
              const snippetParams = parseParamsToSnippet(func.params);
              const insertText = snippetParams
                ? `${moduleName}.${func.name}(${snippetParams})`
                : `${moduleName}.${func.name}()`;
              const callSuggestion: CompletionItem = {
                label: `${moduleName}.${func.name}`,
                kind: monaco.languages.CompletionItemKind.Function,
                documentation: func.docstring
                  ? `[커스텀: ${lib.name}]\n\n${func.docstring}`
                  : `[커스텀: ${lib.name}]\n\nFunction from ${lib.name}`,
                insertText,
                insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                range: range,
                sortText: `1_${moduleName}.${func.name}`,
              };

              const importSuggestion: CompletionItem = {
                label: `from ${moduleName} import ${func.name}`,
                kind: monaco.languages.CompletionItemKind.Module,
                documentation: `Import ${func.name} from ${lib.name}`,
                insertText: `from ${moduleName} import ${func.name}`,
                range: range,
                sortText: `2_from_${moduleName}_import_${func.name}`,
              };

              return [callSuggestion, importSuggestion];
            });
          });

          return { suggestions: customSuggestions };
        },
      });
    },
    [customLibraries]
  );

  useEffect(() => {
    if (readOnly) {
      completionProviderRef.current?.dispose();
      completionProviderRef.current = null;
      return;
    }

    if (monacoRef.current) {
      registerCustomCompletions(monacoRef.current);
    }
  }, [customLibraries, readOnly, registerCustomCompletions]);

  useEffect(() => {
    return () => {
      completionProviderRef.current?.dispose();
      langServiceRef.current?.dispose();
    };
  }, []);

  useEffect(() => {
    if (!isEditorReady || !monacoRef.current || !editorRef.current || readOnly || !clientId) {
      return;
    }

    if (langServiceRef.current) {
      langServiceRef.current.dispose();
    }

    const langService = new PythonLanguageService(
      monacoRef.current as unknown as typeof import('monaco-editor'),
      clientId,
      target,
      () => signature,
      () => editorRef.current?.getValue() ?? code,
      () => autoImports
    );

    langService.registerCompletionProvider();
    langService.registerHoverProvider();
    langService.registerSignatureProvider();

    langServiceRef.current = langService;

    const model = editorRef.current.getModel();
    if (model) {
      langService.updateDiagnostics(model);
    }

    return () => {
      langService.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditorReady, clientId, readOnly, autoImports, signature, target]);

  useEffect(() => {
    if (!langServiceRef.current || !editorRef.current || readOnly) return;

    const model = editorRef.current.getModel();
    if (!model) return;

    const timeoutId = setTimeout(() => {
      langServiceRef.current?.updateDiagnostics(model);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [code, readOnly]);

  const handleEditorMount = useCallback<OnMount>(
    (editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;

      if (!readOnly) {
        registerCustomCompletions(monaco);
      }
      setIsEditorReady(true);
    },
    [readOnly, registerCustomCompletions]
  );

  return (
    <div className={`${styles.codeCell} ${readOnly ? styles.readOnly : ''}`} style={editorStyle}>
      {readOnly ? <div className={styles.readOnlyOverlay} aria-hidden /> : null}
      <div className={styles.editorLayer}>
        <Editor
          height={editorHeight}
          defaultLanguage="python"
          theme={readOnly ? READONLY_CODE_CELL_THEME : CODE_CELL_THEME}
          value={code}
          onChange={onCodeChange ? (value) => onCodeChange(value || '') : undefined}
          beforeMount={defineCodeCellThemes}
          onMount={handleEditorMount}
          options={{
            readOnly,
            minimap: { enabled: false },
            fontSize: 14,
            lineHeight: 24,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            suggestOnTriggerCharacters: !readOnly,
            quickSuggestions: !readOnly,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            domReadOnly: readOnly,
            renderValidationDecorations: readOnly ? 'off' : 'on',
            glyphMargin: false,
            scrollbar:
              readOnly && !scrollableReadOnly
                ? {
                    vertical: 'hidden',
                    horizontal: 'hidden',
                    alwaysConsumeMouseWheel: false,
                    handleMouseWheel: false,
                  }
                : undefined,
            overviewRulerLanes: readOnly ? 0 : undefined,
          }}
        />
      </div>
    </div>
  );
};
